import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { MatIconModule } from "@angular/material/icon";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { TranslatePipe } from "../../../../shared/pipes/translate.pipe";
import { TranslateUtils } from "../../../../shared/utils/translate.utils";
import { ChipState } from "../../../../shared/enums/chip-state.enum";
import { NonConformiteFacadeService } from "../../services/non-conformite/non-conformite-facade.service";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import {
  EtatNc,
  GraviteNc,
  NonConformite,
  TypeNc,
} from "../../models/non-conformite.model";

/**
 * Bloc aside réutilisable affichant les NC liées à un tiers ou un article.
 *
 * - Renseigner `tiersId` OU `articleId` (un seul des deux).
 * - Si `showCostSummary` : KPI ouvertes / critiques / coût estimé total.
 * - Si nb NC < 5 : liste inline.
 * - Sinon : 5 plus récentes + bouton "Voir plus" qui dévoile la liste complète.
 */
@Component({
  selector: "vl-nc-aside-block",
  standalone: true,
  imports: [CommonModule, MatIconModule, ChipsComponent, TranslatePipe],
  templateUrl: "./nc-aside-block.component.html",
  styleUrl: "./nc-aside-block.component.scss",
})
export class NcAsideBlockComponent {
  private facade = inject(NonConformiteFacadeService);
  private router = inject(Router);
  private dialogs = inject(DialogsService);

  tiersId = input<string | undefined>(undefined);
  articleId = input<string | undefined>(undefined);
  showCostSummary = input<boolean>(false);
  /** Titre du bloc (par défaut "Non-conformités liées"). */
  title = input<string>(
    TranslateUtils.translate("non_conformite.aside_title"),
  );
  /** Si true, affiche les NC clôturées/annulées aussi (par défaut: actives uniquement). */
  includeAll = input<boolean>(false);

  loading = signal(false);
  ncs = signal<NonConformite[]>([]);
  expanded = signal(false);

  /** Limite de la liste compacte (au-delà → expand). */
  static COMPACT_LIMIT = 5;

  /** NC filtrées et triées (date desc) selon `includeAll`. */
  visibleNcs = computed<NonConformite[]>(() => {
    const list = this.ncs();
    const filtered = this.includeAll()
      ? list
      : list.filter(
          (nc) => nc.etat !== EtatNc.CLOTUREE && nc.etat !== EtatNc.ANNULEE,
        );
    return [...filtered].sort((a, b) => {
      const da = a.dateOuverture
        ? new Date(a.dateOuverture).getTime()
        : 0;
      const db = b.dateOuverture
        ? new Date(b.dateOuverture).getTime()
        : 0;
      return db - da;
    });
  });

  /** True quand la liste dépasse la limite compacte → bouton expand. */
  hasMore = computed(
    () => this.visibleNcs().length > NcAsideBlockComponent.COMPACT_LIMIT,
  );

  /** Liste affichée (5 max si non expand, tout sinon). */
  displayedNcs = computed<NonConformite[]>(() => {
    const list = this.visibleNcs();
    if (!this.hasMore() || this.expanded()) return list;
    return list.slice(0, NcAsideBlockComponent.COMPACT_LIMIT);
  });

  countLabel = computed(() => {
    const total = this.visibleNcs().length;
    return total === 0
      ? null
      : total === 1
        ? `1 ${TranslateUtils.translate("non_conformite.aside_count_singular")}`
        : `${total} ${TranslateUtils.translate("non_conformite.aside_count_plural")}`;
  });

  // KPIs (showCostSummary)
  ouvertesCount = computed(
    () =>
      this.visibleNcs().filter(
        (nc) => nc.etat === EtatNc.OUVERTE || nc.etat === EtatNc.EN_TRAITEMENT,
      ).length,
  );

  critiquesCount = computed(
    () =>
      this.visibleNcs().filter(
        (nc) =>
          nc.gravite === GraviteNc.CRITIQUE &&
          (nc.etat === EtatNc.OUVERTE || nc.etat === EtatNc.EN_TRAITEMENT),
      ).length,
  );

  coutTotal = computed(() =>
    this.visibleNcs().reduce((sum, nc) => sum + (nc.coutEstime ?? 0), 0),
  );

  constructor() {
    effect(
      async () => {
        const tiersId = this.tiersId();
        const articleId = this.articleId();
        if (!tiersId && !articleId) {
          this.ncs.set([]);
          return;
        }
        this.loading.set(true);
        try {
          let list: NonConformite[];
          if (tiersId && articleId) {
            // Les deux fournis : intersection — charge par tiers (axe métier
            // avec moins de NC habituellement) puis filtre par articleId.
            const all = await this.facade.loadByTiers(tiersId);
            list = (all ?? []).filter((nc) => nc.articleId === articleId);
          } else if (tiersId) {
            list = await this.facade.loadByTiers(tiersId);
          } else {
            list = await this.facade.loadByArticle(articleId!);
          }
          this.ncs.set(list ?? []);
        } finally {
          this.loading.set(false);
        }
      },
      { allowSignalWrites: true },
    );
  }

  /** Couleur du chip de gravité (aligné sur non-conformite-card). */
  graviteState(nc: NonConformite): ChipState {
    if (nc.gravite === GraviteNc.CRITIQUE) return "invalid";
    if (nc.gravite === GraviteNc.MAJEURE) return "warn";
    return "secondary";
  }

  /** Libellé court de la gravité. */
  graviteShort(nc: NonConformite): string {
    if (nc.gravite === GraviteNc.CRITIQUE) return "C";
    if (nc.gravite === GraviteNc.MAJEURE) return "M";
    return "m";
  }

  /** Icône de type pour le chip. */
  typeIcon(nc: NonConformite): string {
    switch (nc.type) {
      case TypeNc.RECEPTION:
        return "input";
      case TypeNc.PRODUCTION:
        return "factory";
      case TypeNc.EXPEDITION:
        return "output";
      case TypeNc.RETOUR_CLIENT:
        return "assignment_return";
      case TypeNc.RECLAMATION_CLIENT:
        return "feedback";
      case TypeNc.AUDIT_INTERNE:
        return "fact_check";
      default:
        return "warning";
    }
  }

  /** Ouvre la fiche détail NC (lazy load du dialog pour éviter l'import circulaire). */
  async onOpenNc(nc: NonConformite) {
    const { NcDetailDialogComponent } = await import(
      "../../containers/nc-detail-dialog/nc-detail-dialog.component"
    );
    this.dialogs.open(NcDetailDialogComponent, "min(95vw, 880px)", "90vh", {
      ncId: nc.id,
    });
  }

  /** Navigue vers la liste filtrée. */
  onSeeAll() {
    const queryParams: Record<string, string> = {};
    if (this.tiersId()) queryParams["tiersId"] = this.tiersId()!;
    if (this.articleId()) queryParams["articleId"] = this.articleId()!;
    this.router.navigate(["/non-conformites"], { queryParams });
  }

  toggleExpand() {
    this.expanded.update((v) => !v);
  }
}
